import { Injectable, inject } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { HttpParamObject } from '../../../shared/data-types/http-types';
import { FileModel } from '../../models/file/file.model';
import {
  NotificationType,
  ShiveNotificationService,
} from '../../services/controls/shive-notification.service';
import * as fromFilesActions from './files.actions';

@Injectable({
  providedIn: 'root',
})
export class FilesHttpService {
  private readonly store = inject(Store);
  private readonly shiveNotificationService = inject(ShiveNotificationService);
  private readonly actions$ = inject(Actions);

  constructor() {
    this.handleHttpCallbacks();
  }

  public getFiles(params: HttpParamObject): Observable<readonly FileModel[]> {
    this.store.dispatch(
      fromFilesActions.fetchFileList({
        payload: {
          params,
        },
      }),
    );

    return this.actions$.pipe(
      ofType(fromFilesActions.setFiles),
      map((result) => result.payload),
      take(1),
    );
  }

  /**
   * @param params must include file_id and kind.
   * @param downloadToDisk control if file should be downloaded to the disk.
   */
  public getSingleFile(params: HttpParamObject, downloadToDisk: boolean): Observable<FileModel> {
    this.store.dispatch(
      fromFilesActions.fetchSingleFile({
        payload: {
          params,
          downloadToDisk,
        },
      }),
    );

    return this.actions$.pipe(
      ofType(fromFilesActions.setSingleFile),
      map((result) => result.payload),
      take(1),
    );
  }

  /**
   * @param fileModel
   * @param params must include file_id and kind.
   */
  public uploadFile(fileModel: FileModel, params: HttpParamObject): Observable<FileModel> {
    this.store.dispatch(
      fromFilesActions.uploadFile({
        payload: {
          fileModel,
          params,
        },
      }),
    );

    return this.actions$.pipe(ofType(fromFilesActions.fileUploaded));
  }

  /**
   * @param params must include file_id and kind.
   */
  public deleteFile(params: HttpParamObject): Observable<FileModel> {
    this.store.dispatch(
      fromFilesActions.deleteFile({
        payload: params,
      }),
    );

    return this.actions$.pipe(ofType(fromFilesActions.fileDeleted));
  }

  private handleHttpCallbacks(): void {
    this.actions$
      .pipe(
        ofType(
          fromFilesActions.fileUploaded,
          fromFilesActions.fileUpdated,
          fromFilesActions.fileDeleted,
        ),
      )
      .subscribe(({ showNotification }) => {
        if (showNotification) {
          this.shiveNotificationService.show(NotificationType.Success);
        }
      });
  }
}
